import { useEffect } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { iamKeys } from './types';

/** The least time between two refetches of a shared list: a focus that flaps (alt-tab, devtools) cannot storm the API. */
export const SHARED_REFETCH_MIN_MS = 30_000;

const SHARED_LIST_KEYS = [iamKeys.mySharedProjects, iamKeys.mySharedWorkspaces] as const;

/** A list answered `lastAt` ms ago (data or error; 0 = never) and is due when it is at least `minMs` old; a clock set back counts as due. */
export function sharedRefetchDue(input: { lastAt: number; now: number; minMs: number }): boolean {
  const { lastAt, now, minMs } = input;
  return lastAt === 0 || now < lastAt || now - lastAt >= minMs;
}

/**
 * Refetches the two shared lists (projects and workspaces other organizations granted) that are on screen and older than
 * `minMs`; returns the keys it asked for. A revoke or a grant made elsewhere shows up on the next focus instead of after
 * the 5 minutes the lists stay fresh. A failed list is retried too (`retry: false` stays, so one focus is one attempt).
 */
export function refetchSharedLists(qc: QueryClient, now = Date.now(), minMs = SHARED_REFETCH_MIN_MS): readonly (readonly unknown[])[] {
  const due = SHARED_LIST_KEYS.filter((queryKey) => {
    const state = qc.getQueryState(queryKey);
    return sharedRefetchDue({ lastAt: Math.max(state?.dataUpdatedAt ?? 0, state?.errorUpdatedAt ?? 0), now, minMs });
  });
  // cancelRefetch:false joins a request already in flight (focus and visibilitychange fire together) instead of aborting it.
  for (const queryKey of due) void qc.invalidateQueries({ queryKey, exact: true }, { cancelRefetch: false });
  return due;
}

/** Refetches the shared lists when the window gets focus or the tab becomes visible again (`refetchSharedLists` rate-limits it). */
export function useRefetchSharedOnFocus(): void {
  const qc = useQueryClient();
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState !== 'hidden') refetchSharedLists(qc);
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [qc]);
}
